import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AspectRatio } from "types/ui";
import {
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeFontVariable,
	ThemeRadiusVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import { Heading } from "@caspeco/casper-ui-library.components.heading";
import { Image } from "@caspeco/casper-ui-library.components.image";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Flex } from "@caspeco/casper-ui-library.components.flex";
import { UpsellArticle } from "types/article";
import { Icons } from "@caspeco/casper-ui-library.components.icon";
import { IconButton } from "@caspeco/casper-ui-library.components.icon-button";
import { Badge } from "@caspeco/casper-ui-library.components.badge";
import Price from "components/price/Price";
import { StyledGridItemButton } from "./UpsellGrid.styles";

const SUBMIT_ITEM_ANIMATION_DURATION_IN_MILLISECONDS = 200;

interface IUpsellGridItem {
	item: UpsellArticle;
	isSubmitting: boolean;
	onAddToCart: (item: UpsellArticle) => Promise<void>;
	onOpenArticle: (articleId: string, articleName: string) => void;
	quantity: number;
}

export function UpsellGridItem({
	item,
	onAddToCart,
	isSubmitting,
	onOpenArticle,
	quantity,
}: IUpsellGridItem) {
	const { t } = useTranslation();
	const [isSubmittingThisItem, setIsSubmittingThisItem] = useState<boolean>(false);

	const {
		id,
		name,
		inHousePriceIncludingVat,
		takeAwayPriceIncludingVat,
		imageMap,
		requiresArticleDetailsPrompt,
		addableToCart,
	} = item;

	const buttonAriaLabel = requiresArticleDetailsPrompt ? t("action_show_more") : t("action_add");

	const onClickButton = async () => {
		// Article requires to be opened in seperate Article Details Modal
		if (requiresArticleDetailsPrompt || !addableToCart) {
			onOpenArticle(item.id, item.name);
			return;
		}
		// No requirements, item can be added to cart
		try {
			setIsSubmittingThisItem(true);
			if (onAddToCart) {
				await onAddToCart(item);
			}
			// Delay end of submission to display button loading animation
			setTimeout(() => {
				setIsSubmittingThisItem(false);
			}, SUBMIT_ITEM_ANIMATION_DURATION_IN_MILLISECONDS);
		} catch {
			setIsSubmittingThisItem(false);
		}
	};

	const onClickCard = () => {
		onOpenArticle(item.id, item.name);
	};

	return (
		<>
			<Box
				as="article"
				display="flex"
				flexDirection="column"
				justifyContent="space-between"
				h="100%"
				position="relative"
			>
				<StyledGridItemButton
					type="button"
					onClick={onClickCard}
					disabled={isSubmitting}
					aria-label={t("action_show_more")}
				/>
				<Box mb={ThemeSpaceVariable.XSmall}>
					<Flex align="start" gap={ThemeSpaceVariable.XSmall}>
						{quantity > 0 && <Badge.Secondary solid>{`${quantity} x`}</Badge.Secondary>}
						<Heading
							as="h3"
							fontWeight="medium"
							wordBreak="break-word"
							color={ThemeColorVariable.OnSurfaceHeader}
							fontFamily={ThemeFontVariable.SubHeader}
							textAlign="left"
							fontSize={{
								base: ThemeFontSizeVariable.Large,
								lg: ThemeFontSizeVariable.XLarge,
							}}
						>
							{name}
						</Heading>
					</Flex>
					<Price
						fontWeight="medium"
						color={ThemeColorVariable.Secondary}
						fontFamily={ThemeFontVariable.SubHeader}
						fontSize={{
							base: ThemeFontSizeVariable.Large,
							lg: ThemeFontSizeVariable.XLarge,
						}}
						inHousePrice={inHousePriceIncludingVat}
						takeAwayPrice={takeAwayPriceIncludingVat}
						textAlign="left"
					/>
				</Box>
				<Flex>
					{imageMap.hasImage(AspectRatio.SixteenByNine) && (
						<Image
							id={`upsell-griditem-image-${id}`}
							src={imageMap.withSizeQuery(AspectRatio.SixteenByNine, "m")}
							srcSet={imageMap.getScrSet(AspectRatio.SixteenByNine, "m", "xl")}
							fallback={
								<Box
									minH={["90px", "150px", "100px", "125px"]}
									w="100%"
									bg={ThemeColorVariable.SurfaceDisabled}
									borderRadius={ThemeRadiusVariable.Medium}
								/>
							}
							borderRadius={ThemeRadiusVariable.Medium}
						/>
					)}
					<Flex
						justifyContent="flex-end"
						position="absolute"
						right={ThemeSpaceVariable.XSmall}
						bottom={ThemeSpaceVariable.XSmall}
					>
						<IconButton
							isDisabled={isSubmitting}
							isLoading={isSubmittingThisItem}
							aria-label={buttonAriaLabel}
							icon={Icons.Add}
							onClick={onClickButton}
						/>
					</Flex>
				</Flex>
			</Box>
		</>
	);
}
